import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ErrorStatus, WarningStatus } from '@sas/react-svg';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { EarconName, useEarcon } from '../../hooks/useEarcon';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { BrowserName, getBrowserName } from '../../util';
import { getScrollbarSize } from '../../util/dom';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { useDirectSpeech } from '../DirectSpeech';
import { Icon } from '../Icon';
import { MessagePopover } from '../Input/__internal__/MessagePopover';
import { GlobalSettingsKeys } from '../Settings/GlobalSettingsKeys';
import { useGetGlobalSetting } from '../Settings/GlobalSettingsProvider';
import bundle from './NovaComponents-TextArea-gui-icu.properties';
import cn from './TextArea.css.json';
import './TextArea.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
export const TextArea = /*#__PURE__*/forwardRef(function TextArea(props, ref) {
  const [hasFocus, setHasFocus] = useState(false);
  const [hasHover, setHasHover] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [shouldSelect, setShouldSelect] = useState(false);
  const [showRequiredError, setShowRequiredError] = useState(false);
  const [statusIconOffset, setStatusIconOffset] = useState('1px');
  const textAreaRef = useRef(null);
  const textLengthWhenImeStartRef = useRef(0);
  const earcon = useEarcon({
    source: EarconName.InputFull
  });
  const {
    disabled,
    readOnly,
    required,
    value,
    autoExpand
  } = props;

  // The reason storing the required TA's status is that the TA will rerender when hovering or focus the TA, and the previous status should be active.
  const status = props.status ?? (showRequiredError ? 'alert' : null);
  {
    // NOTE: this select the text in the textbox when the user changes the focus in the OptionPopper.
    //     | The main issue with this approach is we assume the text in the textbox will change before this happens.
    useLayoutEffect(function selectValue() {
      if (disabled || readOnly || !shouldSelect) return;
      setShouldSelect(false);
      setTimeout(() => {
        const textarea = textAreaRef.current;
        if (textarea && !props.disableAutoSelection) {
          textarea.setSelectionRange(0, textarea.value?.length, undefined);
        }
      }, 0);
    }, [shouldSelect, readOnly, disabled, props.disableAutoSelection]);
  }
  let onBlur, onFocus;
  {
    const handleFocusOut = () => {
      if (disabled || readOnly) return;
      setHasFocus(false);
      // TODO: don't need to fire if list item was clicked, we currently fire twice in that case.
      if (props.onCommit) {
        props.onCommit({
          value
        });
      }
    };
    onBlur = () => {
      if (disabled || readOnly) return;
      setHasHover(false);
      setHasFocus(false);
      setIsTyping(false);
      setShowRequiredError(!!required && value?.length < 1);
      if (props.onBlur) props.onBlur();
      handleFocusOut();
    };
    onFocus = e => {
      if (disabled || readOnly) return;
      setHasFocus(true);
      setShouldSelect(true);
      if (props.onFocus) props.onFocus(e);
    };
  }
  function handleChange(e) {
    if (disabled || readOnly) return;
    const liveValue = e.target.value;
    setShowRequiredError(false);
    setIsTyping(true);
    props.onChange?.({
      value: liveValue
    });
  }
  useEffect(() => {
    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/change_event
    // change value using js does not trigger the onchange event.
    if (!required || !showRequiredError || hasFocus || !value) return;
    setShowRequiredError(false);
  }, [required, showRequiredError, hasFocus, value]);
  const handleMouseEnter = () => setHasHover(true);
  const handleMouseLeave = () => setHasHover(false);
  const iconType = status === 'alert' ? ErrorStatus : status === 'warning' ? WarningStatus : undefined;
  const dt = useDesignTokens();
  const textAreaTokens = {
    ...renderInlineCssVars({
      ...getTextAreaTokens(dt, props, status),
      'bd-width': `0 0 ${dt.tokens['size-border-width-s']} 0`,
      pd: `${dt.tokens['space-base-xs']} ${dt.tokens['space-base-s']}`,
      'mg-xs': dt.tokens['space-base-xs'],
      'font-size-m': dt.tokens['font-size-body-text-m'],
      'editable-focus-outline': 'var(--focus-light-bg)',
      'status-icon-offset': statusIconOffset,
      'bottom-bd-inset-block-end': '1px',
      'bottom-bd-anim-dur': dt.tokens['duration-300'],
      'bottom-bd-anim-tm-ease-in': dt.tokens['ease-in'],
      'bottom-bd-anim-tm-ease-out': dt.tokens['ease-out'],
      'selection-fg': dt.tokens['color-highlight-text-inverse'],
      'selection-bg': dt.tokens['color-highlight-text']
    }, 'textarea'),
    ...renderInlineCssVars({
      fg: dt.tokens['color-action-text-control-placeholder'],
      'disabled-fg': dt.tokens['color-action-text-control-disabled']
    }, 'textarea-placeholder'),
    ...renderInlineCssVars({
      ...(status === 'alert' || status === 'warning' ? {
        fg: dt.tokens[`color-action-text-control-normal`],
        bg: dt.tokens[`color-action-background-control-${status}`],
        bd: dt.tokens[`color-action-border-bottom-control-${status}`]
      } : {
        fg: dt.tokens['color-action-text-control-normal'],
        bg: dt.tokens['color-action-background-control-selected'],
        bd: dt.tokens['color-action-border-bottom-control-selected']
      })
    }, 'textarea-focus')
  };
  const ariaDescribedBy = [];
  if (props.ariaDescribedBy) ariaDescribedBy.push(...props.ariaDescribedBy);
  const directSpeech = useDirectSpeech();
  const localize = useTextLocalizer();
  const message = required ? props.message ?? localize('required.invalidInput.label') : props.message ?? '';
  useEffect(() => {
    if (isTyping) return;
    if (hasFocus && value) directSpeech?.notify(value);else if (status) directSpeech?.notify(localize('invalidInput.label', {
      message
    }));
  }, [isTyping, hasFocus, value, status, directSpeech, localize, message]);
  const updateStatusIconOffset = useCallback(() => {
    if (value && status && iconType && textAreaRef.current && textAreaRef.current.offsetHeight < textAreaRef.current.scrollHeight) {
      const size = getScrollbarSize();
      setStatusIconOffset(`${size ? size : 1}px`);
    } else {
      setStatusIconOffset(`1px`);
    }
  }, [value, status, iconType]);
  useEffect(() => {
    updateStatusIconOffset();
  }, [updateStatusIconOffset]);
  useEffect(() => {
    updateHeight(autoExpand, textAreaRef.current);
  }, [autoExpand, value, localize]);
  let timer;
  const [setTextAreaResizeRef] = useResizeObserverRef(() => {
    clearTimeout(timer);
    timer = setTimeout(function () {
      clearTimeout(timer);
      timer = undefined;
      updateHeight(autoExpand, textAreaRef.current);
      updateStatusIconOffset();
    }, 20);
  });
  const setTextAreaRef = useCallback(textArea => {
    textAreaRef.current = textArea;
    setRef(ref, textArea);
    setTextAreaResizeRef(textArea);
  }, [ref, setTextAreaResizeRef]);
  const playEarcon = textLength => {
    if (textAreaRef.current) {
      const start = textAreaRef.current.selectionStart;
      const end = textAreaRef.current.selectionEnd;
      if (props.maxLength && start + textLength - end >= props.maxLength) earcon.play();
    }
  };
  const shouldPlayAnimation = useGetGlobalSetting(GlobalSettingsKeys.PLAY_VISUAL_EFFECTS) === 'true';
  const iconAnimation = !shouldPlayAnimation ? undefined : {
    closed: {
      opacity: 0
    },
    open: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0, 0.5, 0.2, 1]
      }
    },
    exit: {
      opacity: 0,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0.5, 0, 0.8, 1]
      }
    }
  };
  useEffect(() => {
    const element = textAreaRef.current;
    if (!autoExpand || element === null) return;
    if (element.parentElement && getComputedStyle(element.parentElement).maxHeight !== 'none') {
      // consumer set max-height style on the container
      element.style.maxHeight = 'inherit';
      return;
    }
    if (props.maxRows === undefined || props.maxRows < 1) return;
    const elementStyles = getComputedStyle(element);
    element.style.maxHeight = `${parseFloat(elementStyles.lineHeight) * props.maxRows + parseFloat(elementStyles.paddingTop) + parseFloat(elementStyles.paddingBottom) + parseFloat(elementStyles.borderTopWidth) + parseFloat(elementStyles.borderBottomWidth)}px`;
  }, [autoExpand, props.maxRows, props.style]);
  return /*#__PURE__*/_jsxs("div", {
    "data-testid": props.testId,
    className: cx(cn.container, props.height ? cn.fixedHeight : '', statusIconOffset !== '1px' && cn.withScrollbar, !readOnly && !disabled && cn.editable, hasFocus && cn.focused, props.className),
    onBlur: onBlur,
    onFocus: onFocus,
    style: {
      height: props.height,
      width: props.width,
      ...textAreaTokens,
      ...props.style
    },
    children: [/*#__PURE__*/_jsx("textarea", {
      "data-testid": postfixTestId(props.testId, 'textarea'),
      tabIndex: props.tabIndex ?? undefined,
      ref: setTextAreaRef,
      className: cx(cn.textarea, readOnly && cn.readOnly, disabled && cn.disabled, !hasFocus && status && cn.statusFocusout),
      style: {
        lineHeight: props.style?.lineHeight
      },
      readOnly: !!readOnly || !!disabled,
      "aria-required": !!required,
      "aria-haspopup": props.ariaHasPopup,
      rows: props.rows ? props.rows : undefined,
      placeholder: props.placeholder ? props.placeholder : undefined,
      maxLength: props.maxLength ? props.maxLength : undefined,
      onChange: handleChange,
      onKeyDown: e => {
        const isCtrlOrMetaKey = e.ctrlKey || e.metaKey;
        if (!readOnly && e.key === 'Enter') {
          e.stopPropagation();
        }
        if (textAreaRef.current && !disabled && !readOnly && !isCtrlOrMetaKey &&
        // ignore the unprintable characters
        e.key.length === 1) {
          playEarcon(value?.length);
        }
        props.onKeyDown && props.onKeyDown(e);
      },
      onKeyUp: e => {
        if (disabled || readOnly) return;
        if (e.key === 'Enter' && e.ctrlKey && props.onCommit) {
          // user is committing the current value
          // TODO: do we need to reset the selected index too?
          props.onCommit({
            value
          });
        }
      },
      onCompositionStart: () => {
        if (textAreaRef.current && value) {
          const start = textAreaRef.current.selectionStart;
          const end = textAreaRef.current.selectionEnd;
          textLengthWhenImeStartRef.current = value.length + start - end;
        }
      },
      onCompositionEnd: e => {
        // the IME consideration
        if (props.maxLength && textLengthWhenImeStartRef.current + e.data?.length > props.maxLength) earcon.play();
        textLengthWhenImeStartRef.current = 0;
      },
      onPaste: e => {
        const text = e.clipboardData.getData('text');
        const length = value?.length + text.length;
        playEarcon(length > 0 ? length - 1 : length);
      },
      onMouseUp: e => {
        props.onMouseUp?.(e);
      },
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      autoComplete: "off",
      value: value,
      "aria-disabled": !!disabled,
      "aria-label": props.ariaLabel,
      "aria-labelledby": props.ariaLabelledBy,
      "aria-describedby": ariaDescribedBy.length > 0 ? ariaDescribedBy.join(' ') : undefined,
      id: props.id
    }), /*#__PURE__*/_jsx(AnimatePresence, {
      children: !hasFocus && status && iconType ? /*#__PURE__*/_jsx(motion.div, {
        className: cn.statusIcon,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        variants: iconAnimation,
        initial: "closed",
        animate: "open",
        exit: "exit",
        children: /*#__PURE__*/_jsx(Icon, {
          icon: iconType,
          color: status
        })
      }) : null
    }), !status || !message ? null : /*#__PURE__*/_jsx(MessagePopover, {
      status: status,
      referenceElementRef: textAreaRef,
      message: message,
      isOpen: hasFocus || hasHover,
      testId: postfixTestId(props.testId, 'messagePopper')
    })]
  });
});
function getTextAreaTokens(dt, props, status) {
  if (props.disabled) {
    return {
      fg: dt.tokens['color-action-text-control-disabled'],
      bg: dt.tokens['color-action-background-control-disabled'],
      bd: dt.tokens['color-action-border-control-disabled'],
      'hover-fg': 'var(--textarea-fg)',
      'hover-bg': 'var(--textarea-bg)',
      'hover-bd': 'var(--textarea-bd)',
      'disabled-focus-bd': dt.themeVariant !== 'high-contrast'
    };
  } else if (props.readOnly) {
    return {
      fg: dt.tokens['color-action-text-control-read-only'],
      bg: dt.tokens['color-action-background-control-read-only'],
      bd: dt.tokens['color-action-border-control-read-only'],
      'hover-fg': 'var(--textarea-fg)',
      'hover-bg': 'var(--textarea-bg)',
      'hover-bd': 'var(--textarea-bd)'
    };
  } else {
    switch (status) {
      case 'alert':
        return {
          fg: dt.tokens['color-action-text-control-alert'],
          bg: dt.tokens['color-action-background-control-alert'],
          bd: dt.tokens['color-action-border-bottom-control-alert'],
          'hover-fg': 'var(--textarea-fg)',
          'hover-bg': 'var(--textarea-bg)',
          'hover-bd': 'var(--textarea-bd)'
        };
      case 'warning':
        return {
          fg: dt.tokens['color-action-text-control-warning'],
          bg: dt.tokens['color-action-background-control-warning'],
          bd: dt.tokens['color-action-border-bottom-control-warning'],
          'hover-fg': 'var(--textarea-fg)',
          'hover-bg': 'var(--textarea-bg)',
          'hover-bd': 'var(--textarea-bd)'
        };
      default:
        return {
          fg: dt.tokens['color-action-text-control-normal'],
          bg: dt.tokens['color-action-background-control-normal'],
          bd: dt.tokens['color-action-border-bottom-control-normal'],
          'hover-fg': dt.tokens['color-action-text-control-hover'],
          'hover-bg': dt.tokens['color-action-background-control-hover'],
          'hover-bd': dt.tokens['color-action-border-bottom-control-hover']
        };
    }
  }
}
const updateHeight = (autoExpand, element) => {
  if (autoExpand && element) {
    const buddy = element.cloneNode(true);
    buddy.style.position = 'absolute';
    // scrollbar will occupy some space, try best to not scroll
    buddy.style.overflowY = 'hidden';
    // When value changed shorter, we need to set auto height first to let it shrink
    buddy.style.height = 'auto';
    element.parentElement?.append(buddy);
    if (getBrowserName() === BrowserName.Firefox) buddy.scrollHeight; // HACK: buddy.scrollHeight needs to be excuted twice, or its value will be wrong.
    const scrollHeight = buddy.scrollHeight;
    element.parentElement?.removeChild(buddy);
    element.style.overflowY = 'hidden'; // HACK: if not, the textarea may have a scrollbar.
    const elementStyles = getComputedStyle(element);
    const borderWidth = parseFloat(elementStyles.borderTopWidth) + parseFloat(elementStyles.borderBottomWidth);
    // when zooming in the window, the value of offsetWidth - clientWidth may be 1px not 2px,
    // this may cause a scrollbar show in the textarea, and at this time the height of the textarea did not reach the max-height.
    element.style.height = `${Math.ceil(scrollHeight + borderWidth)}px`;
    // recover after calculation
    element.style.overflowY = 'auto';
  }
};
//# sourceMappingURL=TextArea.js.map